import type { ModeDefinition } from './types';

function randomHexColor(): string {
  return `#${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0')}`;
}

export const nightSkyMode: ModeDefinition = {
  id: 'night-sky',
  name: 'Night Sky',
  modeIndex: 0,
  groups: [
    { id: 'palette', label: 'Palette', collapsible: true, collapsedByDefault: true },
    { id: 'nebula', label: 'Nebula', collapsible: true, collapsedByDefault: true },
    { id: 'bgstars', label: 'Background Stars', collapsible: true, collapsedByDefault: true },
    { id: 'herostars', label: 'Hero Stars', collapsible: true, collapsedByDefault: true },
    { id: 'sun', label: 'Sun', collapsible: true, collapsedByDefault: true },
    { id: 'tone', label: 'Tone Mapping', collapsible: true, collapsedByDefault: true },
  ],
  params: [
    // Palette
    {
      id: 'primaryRamp',
      type: 'color-ramp',
      label: 'Primary Ramp',
      group: 'palette',
      default: ['#0b141f', '#214c6b', '#9ff2ff'],
      tooltip: 'Main nebula colors from dark to bright',
    },
    {
      id: 'secondaryRamp',
      type: 'color-ramp',
      label: 'Secondary Ramp',
      group: 'palette',
      default: ['#120a10', '#4a1d2a', '#ffb08a'],
      tooltip: 'Secondary nebula region colors',
    },
    {
      id: 'accentRamp',
      type: 'color-ramp',
      label: 'Accent Ramp',
      group: 'palette',
      default: ['#0b0a12', '#3b1450', '#ff7bd5'],
      tooltip: 'Accent highlight colors',
    },
    {
      id: 'variation',
      type: 'slider',
      label: 'Color Variation',
      group: 'palette',
      tooltip: 'Blends in secondary and accent palette ramps',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.55,
    },

    // Nebula
    {
      id: 'nebula',
      type: 'slider',
      label: 'Nebula Intensity',
      group: 'nebula',
      tooltip: 'Overall brightness and strength of the nebula layers',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.7,
    },
    {
      id: 'coverage',
      type: 'slider',
      label: 'Nebula Coverage',
      group: 'nebula',
      tooltip: 'How much of the sky is covered by nebula volume',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.8,
      lockedByDefault: true,
    },
    {
      id: 'detail',
      type: 'slider',
      label: 'Nebula Detail',
      group: 'nebula',
      tooltip: 'Higher values add more filament and dust structure',
      min: 0.2,
      max: 1.5,
      step: 0.01,
      default: 0.85,
    },
    {
      id: 'haze',
      type: 'slider',
      label: 'Nebula Haze',
      group: 'nebula',
      tooltip: 'Softens the nebula and adds ambient glow',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.25,
    },

    // Background Stars
    {
      id: 'starDensity',
      type: 'slider',
      label: 'Density',
      group: 'bgstars',
      tooltip: 'Number of background stars',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.6,
    },
    {
      id: 'starGlow',
      type: 'slider',
      label: 'Glow',
      group: 'bgstars',
      tooltip: 'Intensity and size of star halos',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.35,
    },

    // Hero Stars
    {
      id: 'heroCount',
      type: 'slider',
      label: 'Count',
      group: 'herostars',
      tooltip: 'Number of large hero stars with diffraction spikes',
      min: 0,
      max: 100,
      step: 1,
      default: 50,
    },
    {
      id: 'heroBrightness',
      type: 'slider',
      label: 'Brightness',
      group: 'herostars',
      tooltip: 'Brightness of hero stars',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      id: 'heroSize',
      type: 'slider',
      label: 'Size',
      group: 'herostars',
      tooltip: 'Size of hero stars and their diffraction spikes',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },

    // Sun (for space skyboxes)
    {
      id: 'sunEnabled',
      type: 'toggle',
      label: 'Enabled',
      group: 'sun',
      default: false,
      tooltip: 'Show sun(s) in the sky (for space/alien skyboxes)',
    },
    {
      id: 'sunCount',
      type: 'slider',
      label: 'Count',
      group: 'sun',
      tooltip: 'Number of suns (for alien worlds)',
      min: 1,
      max: 4,
      step: 1,
      default: 1,
    },
    {
      id: 'sunSizeMin',
      type: 'slider',
      label: 'Size Min',
      group: 'sun',
      tooltip: 'Minimum size of sun discs',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.4,
    },
    {
      id: 'sunSizeMax',
      type: 'slider',
      label: 'Size Max',
      group: 'sun',
      tooltip: 'Maximum size of sun discs',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.6,
    },
    {
      id: 'sunIntensity',
      type: 'slider',
      label: 'Brightness',
      group: 'sun',
      tooltip: 'Brightness of the sun',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.7,
    },

    // Tone mapping
    {
      id: 'contrast',
      type: 'slider',
      label: 'Contrast',
      group: 'tone',
      tooltip: 'Boosts overall contrast in the final output',
      min: 0.8,
      max: 1.4,
      step: 0.01,
      default: 1.08,
      lockedByDefault: true,
    },
    {
      id: 'gamma',
      type: 'slider',
      label: 'Gamma',
      group: 'tone',
      tooltip: 'Adjusts midtone brightness after contrast',
      min: 0.6,
      max: 1.3,
      step: 0.01,
      default: 0.95,
      lockedByDefault: true,
    },
  ],

  randomize(currentValues, lockedParams) {
    const values = { ...currentValues };

    // Randomize colors
    if (!lockedParams.has('primaryRamp')) {
      values.primaryRamp = [randomHexColor(), randomHexColor(), randomHexColor()];
    }
    if (!lockedParams.has('secondaryRamp')) {
      values.secondaryRamp = [randomHexColor(), randomHexColor(), randomHexColor()];
    }
    if (!lockedParams.has('accentRamp')) {
      values.accentRamp = [randomHexColor(), randomHexColor(), randomHexColor()];
    }

    // Randomize sliders (respecting locks)
    if (!lockedParams.has('variation')) {
      values.variation = 0.35 + Math.random() * 0.55;
    }
    if (!lockedParams.has('nebula')) {
      values.nebula = 0.4 + Math.random() * 0.6;
    }
    if (!lockedParams.has('coverage')) {
      values.coverage = 0.2 + Math.random() * 0.8;
    }
    if (!lockedParams.has('detail')) {
      values.detail = 0.5 + Math.random() * 1.0;
    }
    if (!lockedParams.has('haze')) {
      values.haze = Math.random() * 0.45;
    }
    if (!lockedParams.has('starDensity')) {
      values.starDensity = 0.15 + Math.random() * 0.5;
    }
    if (!lockedParams.has('starGlow')) {
      values.starGlow = 0.15 + Math.random() * 0.7;
    }
    if (!lockedParams.has('heroCount')) {
      values.heroCount = Math.floor(20 + Math.random() * 60);
    }
    if (!lockedParams.has('heroBrightness')) {
      values.heroBrightness = 0.3 + Math.random() * 0.5;
    }
    if (!lockedParams.has('heroSize')) {
      values.heroSize = 0.3 + Math.random() * 0.5;
    }
    // Randomize sun (occasionally visible for space skyboxes)
    if (!lockedParams.has('sunEnabled')) {
      values.sunEnabled = Math.random() < 0.15;
    }
    if (!lockedParams.has('sunCount')) {
      values.sunCount = Math.random() < 0.2 ? Math.floor(2 + Math.random() * 2) : 1;
    }
    if (!lockedParams.has('sunSizeMin')) {
      values.sunSizeMin = 0.2 + Math.random() * 0.4;
    }
    if (!lockedParams.has('sunSizeMax')) {
      values.sunSizeMax = Math.max((values.sunSizeMin as number) + 0.1, 0.4 + Math.random() * 0.4);
    }
    if (!lockedParams.has('sunIntensity')) {
      values.sunIntensity = 0.5 + Math.random() * 0.4;
    }
    if (!lockedParams.has('contrast')) {
      values.contrast = 0.95 + Math.random() * 0.35;
    }
    if (!lockedParams.has('gamma')) {
      values.gamma = 0.8 + Math.random() * 0.4;
    }

    return values;
  },
};
